'use client';

import { useRef, useState, useEffect, useCallback, useId } from 'react';
import ReactPlayer from 'react-player/lazy';
import { IconPlayerPauseFilled, IconPlayerPlayFilled } from '@tabler/icons-react';

function formatTime(secs) {
    if (!secs || isNaN(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

/* ── Inline SVG icons ─────────────────────────────────────────── */

function LoopIcon() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true">
            <polyline points="17 1 21 5 17 9" />
            <path d="M3 11V9a4 4 0 0 1 4-4h14" />
            <polyline points="7 23 3 19 7 15" />
            <path d="M21 13v2a4 4 0 0 1-4 4H3" />
        </svg>
    );
}

function VolumeOnIcon() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
        </svg>
    );
}

function VolumeOffIcon() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <line x1="23" y1="9" x2="17" y2="15" />
            <line x1="17" y1="9" x2="23" y2="15" />
        </svg>
    );
}

function MaximizeIcon() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true">
            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
        </svg>
    );
}

function MinimizeIcon() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true">
            <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
        </svg>
    );
}

/* ─────────────────────────────────────────────────────────────── */

const SPEEDS = [1, 1.5, 2];
const HIDE_DELAY = 3000;

export default function PodcastPlayer({ identifier, vtt, autoPlay = false }) {
    const playerRef = useRef(null);
    const containerRef = useRef(null);
    const progressBarRef = useRef(null);
    const hideTimer = useRef(null);
    const isDragging = useRef(false); // mutable ref — no re-renders needed
    const uniqueId = useId();

    const [isClient, setIsClient] = useState(false);
    const [videoUrl, setVideoUrl] = useState('');
    const [playing, setPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(0.8);
    const [progress, setProgress] = useState(0); // played fraction  0–1
    const [loaded, setLoaded] = useState(0); // buffered fraction 0–1
    const [playedSeconds, setPlayedSeconds] = useState(0);
    const [totalSeconds, setTotalSeconds] = useState(0);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [isLooping, setIsLooping] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [controlsVisible, setControlsVisible] = useState(true);
    const [seekFraction, setSeekFraction] = useState(null); // non-null while dragging

    /*
     * While dragging the scrubber we show the cursor position, not the
     * playback position, so the dot and fill feel instant.
     */
    const displayFraction = seekFraction !== null ? seekFraction : progress;

    const config = {
        file: {
            forceReload: true,
            attributes: { crossOrigin: 'anonymous' },
            tracks: vtt
                ? [{ kind: 'subtitles', src: `/dA/${vtt}`, srcLang: 'en', default: false }]
                : []
        }
    };

    useEffect(() => {
        setIsClient(true);
        if (autoPlay) setPlaying(true);
    }, [autoPlay]);

    useEffect(() => {
        if (identifier) setVideoUrl(`/dA/${identifier}`);
    }, [identifier]);

    /* ── Controls auto-hide ───────────────────────────────────── */

    const scheduleHide = useCallback(() => {
        clearTimeout(hideTimer.current);
        hideTimer.current = setTimeout(() => setControlsVisible(false), HIDE_DELAY);
    }, []);

    const showControls = useCallback(() => {
        setControlsVisible(true);
        if (playing) scheduleHide();
    }, [playing, scheduleHide]);

    useEffect(() => {
        if (playing) {
            scheduleHide();
        } else {
            clearTimeout(hideTimer.current);
            setControlsVisible(true);
        }
        return () => clearTimeout(hideTimer.current);
    }, [playing, scheduleHide]);

    /* ── Playback handlers ────────────────────────────────────── */

    const togglePlay = useCallback(() => {
        setPlaying((prev) => {
            if (prev && playerRef.current) setPlayedSeconds(playerRef.current.getCurrentTime());
            return !prev;
        });
    }, []);

    const onPause = useCallback(() => {
        if (playerRef.current) setPlayedSeconds(playerRef.current.getCurrentTime());
    }, []);

    const toggleMute = useCallback(() => setIsMuted((v) => !v), []);
    const toggleLoop = useCallback(() => setIsLooping((v) => !v), []);

    const toggleFullscreen = useCallback(() => {
        if (!containerRef.current) return;
        if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen().then(() => setIsFullscreen(true));
        } else {
            document.exitFullscreen().then(() => setIsFullscreen(false));
        }
    }, []);

    /* ── Progress bar — pointer-capture drag ──────────────────── */

    const getFraction = useCallback((clientX) => {
        if (!progressBarRef.current) return 0;
        const rect = progressBarRef.current.getBoundingClientRect();
        return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    }, []);

    const handlePointerDown = useCallback(
        (e) => {
            e.preventDefault();
            e.currentTarget.setPointerCapture(e.pointerId);
            isDragging.current = true;
            setSeekFraction(getFraction(e.clientX));
        },
        [getFraction]
    );

    const handlePointerMove = useCallback(
        (e) => {
            if (!isDragging.current) return;
            setSeekFraction(getFraction(e.clientX));
        },
        [getFraction]
    );

    const handlePointerUp = useCallback(
        (e) => {
            if (!isDragging.current) return;
            isDragging.current = false;
            const fraction = getFraction(e.clientX);
            setSeekFraction(null);
            playerRef.current?.seekTo(fraction, 'fraction');
        },
        [getFraction]
    );

    return (
        <div
            ref={containerRef}
            className="relative size-full overflow-hidden bg-black"
            onMouseMove={showControls}
            onMouseLeave={() => playing && setControlsVisible(false)}
            onTouchStart={showControls}>
            {/* ReactPlayer */}
            {isClient && (
                <ReactPlayer
                    key={videoUrl}
                    ref={playerRef}
                    url={videoUrl}
                    playing={playing}
                    muted={isMuted}
                    loop={isLooping}
                    volume={volume}
                    playbackRate={playbackRate}
                    config={config}
                    width="100%"
                    height="100%"
                    style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
                    onPause={onPause}
                    onProgress={({ played, playedSeconds: ps, loaded: buf }) => {
                        if (!isDragging.current) setProgress(played);
                        setPlayedSeconds(ps ?? 0);
                        setLoaded(buf ?? 0);
                    }}
                    onDuration={(d) => setTotalSeconds(d)}
                    onEnded={() => {
                        setPlaying(false);
                        setPlayedSeconds(0);
                        setProgress(0);
                    }}
                    onError={(e) => console.error('PodcastPlayer error:', e)}
                    progressInterval={250}
                />
            )}

            {/* Click-through play / pause on the video surface */}
            <button
                className="absolute inset-0 size-full cursor-pointer"
                onClick={togglePlay}
                aria-label={playing ? 'Pause' : 'Play'}
            />

            {/* ── Controls overlay ─────────────────────────────────────── */}
            <div
                className="pointer-events-none absolute inset-x-0 bottom-0 px-4 pb-4 pt-20 transition-opacity duration-300"
                style={{
                    background: 'linear-gradient(to top, rgba(0,0,0,0.82) 0%, transparent 100%)',
                    opacity: controlsVisible ? 1 : 0
                }}>
                {/* ── Progress bar ──────────────────────────────────────── */}
                {/*
                 * Outer div is the larger pointer-capture target (py-2 adds hit area).
                 * Inner div is the visual rail — grows slightly on hover.
                 * Layers (back → front): rail bg / buffered / played / scrubber dot.
                 */}
                <div
                    ref={progressBarRef}
                    className="group pointer-events-auto relative mb-3 flex cursor-pointer items-center py-2"
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    style={{ touchAction: 'none' }}>
                    <div className="relative h-[3px] w-full overflow-hidden rounded-full bg-white/15 transition-[height] duration-150 group-hover:h-[5px]">
                        {/* Buffered track */}
                        <div
                            className="absolute inset-y-0 left-0 w-full origin-left bg-white/25"
                            style={{ transform: `scaleX(${loaded})` }}
                        />
                        {/* Played track */}
                        <div
                            className="absolute inset-y-0 left-0 w-full origin-left bg-fuschia"
                            style={{
                                transform: `scaleX(${displayFraction})`,
                                transition: isDragging.current ? 'none' : 'transform 0.25s linear'
                            }}
                        />
                    </div>
                    {/* Scrubber dot — outside the overflow:hidden rail */}
                    <div
                        className="pointer-events-none absolute top-1/2 h-[13px] w-[13px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-[0_0_4px_rgba(0,0,0,0.4)] transition-opacity duration-150"
                        style={{
                            left: `${displayFraction * 100}%`,
                            opacity: seekFraction !== null ? 1 : 0
                        }}
                    />
                    {/* Dot also appears on hover (CSS group-hover handles this) */}
                    <div
                        className="pointer-events-none absolute top-1/2 h-[13px] w-[13px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white opacity-0 shadow-[0_0_4px_rgba(0,0,0,0.4)] transition-opacity duration-150 group-hover:opacity-100"
                        style={{ left: `${displayFraction * 100}%` }}
                    />
                </div>

                {/* ── Controls row ──────────────────────────────────────── */}
                <div className="pointer-events-auto flex items-center justify-between gap-3">
                    {/* Left: play/pause + time */}
                    <div className="flex items-center gap-3">
                        <button
                            onClick={togglePlay}
                            className="flex size-8 shrink-0 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/20 backdrop-blur-sm transition-colors hover:bg-white/25"
                            aria-label={playing ? 'Pause' : 'Play'}>
                            {playing ? (
                                <IconPlayerPauseFilled className="size-[14px] text-white" />
                            ) : (
                                <IconPlayerPlayFilled className="size-[14px] text-white" />
                            )}
                        </button>

                        <span className="text-xs tabular-nums text-white/50">
                            {formatTime(playedSeconds)}
                            {totalSeconds > 0 && (
                                <span className="text-white/30"> / {formatTime(totalSeconds)}</span>
                            )}
                        </span>
                    </div>

                    {/* Right: mute, volume, loop, speed, fullscreen */}
                    <div className="flex items-center gap-3">
                        <button
                            onClick={toggleMute}
                            className="text-white/50 transition-colors hover:text-white"
                            aria-label={isMuted ? 'Unmute' : 'Mute'}>
                            {isMuted ? <VolumeOffIcon /> : <VolumeOnIcon />}
                        </button>

                        <label htmlFor={`vol-${uniqueId}`} className="sr-only">
                            Volume
                        </label>
                        <input
                            id={`vol-${uniqueId}`}
                            type="range"
                            min="0"
                            max="1"
                            step="0.02"
                            value={volume}
                            onChange={(e) => setVolume(parseFloat(e.target.value))}
                            className="hidden w-16 cursor-pointer sm:block"
                            style={{
                                accentColor: '#B626E8',
                                background: `linear-gradient(to right, #B626E8 ${volume * 100}%, rgba(255,255,255,0.15) ${volume * 100}%)`
                            }}
                        />

                        <button
                            onClick={toggleLoop}
                            className={`transition-colors ${isLooping ? 'text-fuschia' : 'text-white/50 hover:text-white'}`}
                            aria-label="Toggle loop">
                            <LoopIcon />
                        </button>

                        <div className="flex items-center rounded-full border border-white/10 bg-white/5">
                            {SPEEDS.map((speed) => (
                                <button
                                    key={speed}
                                    onClick={() => setPlaybackRate(speed)}
                                    className={`rounded-full px-2 py-0.5 text-xs transition-colors ${
                                        playbackRate === speed
                                            ? 'bg-fuschia/30 font-semibold text-white'
                                            : 'text-white/40 hover:text-white/80'
                                    }`}>
                                    {speed}x
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={toggleFullscreen}
                            className="text-white/50 transition-colors hover:text-white"
                            aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
                            {isFullscreen ? <MinimizeIcon /> : <MaximizeIcon />}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
